import {
  DatiSessioneUtente,
  EnumCambiamentoStatoRequestTransizione,
} from '@framework/api-client';
import {
  IModuleStateMachineRecord,
  ModuleStateMachine,
  SubmissionModuleState,
} from '@framework/frontoffice-submission';
import { useCallback } from 'react';
import { useSubmissionModuleStateChanger } from '../cards/module/action-buttons/useSubmissionModuleStateChanger';

interface Params {
  record: IModuleStateMachineRecord;
  user: DatiSessioneUtente | null | undefined;
}

/**
 * Ritorna due funzioni da utilizzare prima e dopo il salvataggio di un modulo.
 * Permettono di eseguire azioni personalizzate prima e dopo il salvataggio.
 *
 * Quando l'utente compilatore è anche il titolare della richiesta e non ci sono deleghe,
 * evitiamo di gestire il completamento del modulo manualmente e lo gestiamo in automatico
 * con le funzioni qui implementate.
 */
export function useModuleSaverPrehooks(params: Params) {
  const { record, user } = params;

  // Possiamo applicare le funzioni di auto-completamento del modulo
  // solo se l'utente ha il pieno controllo (titolare senza delegati)
  // e il modulo non richiede la firma.
  const canAutoChangeState = ModuleStateMachine.can(
    record,
    '.autoComplete',
    user,
  );

  const { handleClick: stateChanger } = useSubmissionModuleStateChanger({
    data: record,
    successMessage: null,
  });

  const onBeforeSave = useCallback(async () => {
    if (!canAutoChangeState.allowed) return;
    // Se il modulo non è ancora completato, non serve fare nulla
    if (record.module?.stato !== SubmissionModuleState.Completed) return;

    // Cambio lo stato del modulo da Completed a Compilation, così da poterlo salvare
    await stateChanger(
      EnumCambiamentoStatoRequestTransizione.RIPORTA_IN_COMPILAZIONE,
      { mantainQueries: true },
    );
  }, [canAutoChangeState, record.module?.stato, stateChanger]);

  const onAfterSave = useCallback(
    async (isValid: boolean | null | undefined) => {
      if (!canAutoChangeState.allowed) return;

      // Se non è valido, non serve fare nulla
      if (!isValid) return;

      // Cambio lo stato del modulo da Completed a Compilation, così da poterlo salvare
      await stateChanger(EnumCambiamentoStatoRequestTransizione.COMPLETA);
    },
    [canAutoChangeState.allowed, stateChanger],
  );

  return { onBeforeSave, onAfterSave };
}
